/* Region layer for the two Aquaculture Kings stores.
 *
 * Replaces assets/geo-prompt.js. Three things changed and each one matters:
 *
 *   1. Country now comes from Shopify's own edge
 *      (/browsing_context_suggestions.json) instead of a third-party IP API.
 *      No extra DNS or TLS handshake, and nothing for an ad blocker to kill.
 *
 *   2. The signal persists. The old prompt set a 30-day dismiss cookie, so a
 *      US visitor who closed it once got no further warning right through to
 *      a checkout that cannot accept their address. Collapsing this one
 *      leaves a permanent strip instead.
 *
 *   3. It knows which store it is running on. The old snippet was shipped
 *      byte-identical to the US store, where it told US visitors to go to the
 *      US store they were already on.
 *
 * Fails quiet: any error and the storefront behaves exactly as if this file
 * were not here.
 */
(function () {
  'use strict';

  var STATE_KEY = 'ak_region_state_v1';
  var COUNTRY_KEY = 'ak_region_country_v1';
  var COUNTRY_TTL = 24 * 60 * 60 * 1000;

  function readJson(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  var config = readJson('ak-region-config');
  var map = readJson('ak-product-map') || { products: [], pages: {} };
  var root = document.querySelector('ak-region');

  if (!config || !root) return;
  if (window.Shopify && window.Shopify.designMode) return;

  // ---- storage helpers, all tolerant of privacy modes ----------------------

  function readStore(key) {
    try {
      var raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeStore(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* private mode, quota, or storage disabled: behave as if uncached */
    }
  }

  // ---- country detection --------------------------------------------------

  function cachedCountry() {
    var hit = readStore(COUNTRY_KEY);
    if (!hit || typeof hit.c !== 'string') return null;
    if (Date.now() - hit.t > COUNTRY_TTL) return null;
    return hit.c;
  }

  function detectCountry() {
    var hit = cachedCountry();
    if (hit) return Promise.resolve(hit);

    return fetch('/browsing_context_suggestions.json', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (data) {
        var country =
          data &&
          data.detected_values &&
          data.detected_values.country &&
          data.detected_values.country.handle;
        if (typeof country !== 'string') return null;
        country = country.toUpperCase();
        writeStore(COUNTRY_KEY, { c: country, t: Date.now() });
        return country;
      })
      .catch(function () {
        return null;
      });
  }

  // ---- map lookups --------------------------------------------------------

  var fromKey = config.store; // 'au' or 'us'
  var toKey = config.other.store;

  function pairFor(handle) {
    for (var i = 0; i < map.products.length; i++) {
      if (map.products[i][fromKey] === handle) return map.products[i];
    }
    return null;
  }

  function counterpartUrl(handle) {
    var pair = pairFor(handle);
    if (!pair) return null;
    return config.other.origin + '/products/' + pair[toKey];
  }

  // ---- region bar ---------------------------------------------------------

  var bar = root.querySelector('[data-ak-region-bar]');
  var strip = root.querySelector('[data-ak-region-strip]');
  var message = root.querySelector('[data-ak-region-message]');
  var goLink = root.querySelector('[data-ak-region-go]');
  var stayBtn = root.querySelector('[data-ak-region-stay]');
  var expandBtn = root.querySelector('[data-ak-region-expand]');
  var stripText = root.querySelector('[data-ak-region-strip-text]');

  var homeLabel = config.store === 'au' ? 'Australian' : 'US';
  var shortLabel = config.store === 'au' ? 'AU store' : 'US store';

  function fillCopy() {
    message.innerHTML =
      'You are on the <b>' +
      homeLabel +
      '</b> store. Prices are in <b>' +
      config.currency +
      '</b> and ' +
      config.shipsCopy +
      '.';

    var deep = config.page.counterpartUrl;
    if (deep) {
      goLink.textContent = 'See this on the ' + config.other.label;
      goLink.href = deep;
    } else {
      goLink.textContent = 'Go to the ' + config.other.label;
      goLink.href = config.other.origin;
    }

    stripText.textContent =
      shortLabel + ' · ' + config.currency + ' · ' + config.shipsCopy;
  }

  function show(state) {
    root.hidden = false;
    if (state === 'strip') {
      bar.hidden = true;
      strip.hidden = false;
    } else {
      bar.hidden = false;
      strip.hidden = true;
    }
  }

  function setState(state) {
    writeStore(STATE_KEY, { s: state, t: Date.now() });
    show(state);
  }

  stayBtn.addEventListener('click', function () {
    setState('strip');
  });

  expandBtn.addEventListener('click', function () {
    setState('expanded');
  });

  // ---- cart interstitial --------------------------------------------------

  function buildCartPlan() {
    var move = [];
    var pick = [];
    var drop = [];

    (config.cart || []).forEach(function (line) {
      var pair = pairFor(line.handle);
      if (!pair) {
        drop.push(line);
      } else if (pair.exact && pair[toKey + '_variant']) {
        move.push({ line: line, variant: pair[toKey + '_variant'] });
      } else {
        pick.push({ line: line, url: config.other.origin + '/products/' + pair[toKey] });
      }
    });

    return { move: move, pick: pick, drop: drop };
  }

  function cartTransferUrl(plan) {
    if (!plan.move.length) return null;
    var parts = plan.move.map(function (entry) {
      return entry.variant + ':' + entry.line.quantity;
    });
    // storefront=true lands on the other store's cart page. Without it a cart
    // permalink jumps straight into checkout, which would skip the review step
    // and hide the fact that some lines could not be carried across.
    return config.other.origin + '/cart/' + parts.join(',') + '?storefront=true';
  }

  function buildCartPanel() {
    var plan = buildCartPlan();
    if (!plan.move.length && !plan.pick.length && !plan.drop.length) return null;

    var panel = document.createElement('div');
    panel.className = 'ak-cart-region';
    panel.setAttribute('data-ak-cart-region', '');

    var heading = document.createElement('p');
    heading.className = 'ak-cart-region__h';
    heading.textContent = 'This store cannot ship to you';
    panel.appendChild(heading);

    var intro = document.createElement('p');
    intro.textContent =
      'You are checking out on the ' +
      homeLabel +
      ' store, and ' +
      config.shipsCopy +
      ', so your address will be refused at the payment step. Here is the same basket on the ' +
      config.other.label +
      ', priced in ' +
      config.other.currency +
      '.';
    panel.appendChild(intro);

    if (plan.move.length || plan.pick.length || plan.drop.length) {
      var list = document.createElement('ul');
      list.className = 'ak-cart-region__list';

      plan.move.forEach(function (entry) {
        list.appendChild(lineRow('ok', '✓', entry.line.title + ' × ' + entry.line.quantity + ', moves across'));
      });
      plan.pick.forEach(function (entry) {
        list.appendChild(lineRow('pick', '!', entry.line.title + ', choose your option on the ' + config.other.label));
      });
      plan.drop.forEach(function (line) {
        list.appendChild(lineRow('no', '×', line.title + ', not sold on the ' + config.other.label + ' yet'));
      });

      panel.appendChild(list);
    }

    var actions = document.createElement('div');
    actions.className = 'ak-cart-region__actions';

    var transfer = cartTransferUrl(plan);
    if (transfer) {
      var move = document.createElement('a');
      move.className = 'ak-cart-region__btn';
      move.href = transfer;
      move.textContent = 'Rebuild my cart on the ' + config.other.label;
      actions.appendChild(move);
    }

    var browse = document.createElement('a');
    browse.className = 'ak-cart-region__btn ak-cart-region__btn--ghost';
    browse.href = config.other.origin;
    browse.textContent = 'Open the ' + config.other.label;
    actions.appendChild(browse);

    panel.appendChild(actions);
    return panel;
  }

  function lineRow(kind, mark, text) {
    var li = document.createElement('li');
    var span = document.createElement('span');
    span.className = 'ak-cart-region__mark ak-cart-region__mark--' + kind;
    span.setAttribute('aria-hidden', 'true');
    span.textContent = mark;
    li.appendChild(span);
    li.appendChild(document.createTextNode(text));
    return li;
  }

  /* The checkout button usually sits in a narrow totals column, and a warning
     squeezed into it is a warning nobody reads. Walk up from the button until
     the ancestor is wide enough to hold a few lines comfortably, then mount
     just above that: still directly before the checkout step, but legible.
     In the cart drawer everything is narrow already, so this stays put. */
  function mountTarget(btn) {
    var boundary = btn.closest('form') || btn.closest('[class*="cart"]') || document.body;
    var wideEnough = Math.max(360, boundary.clientWidth * 0.55);
    var el = btn.parentElement;
    while (el && el !== boundary && el.parentElement && el.clientWidth < wideEnough) {
      el = el.parentElement;
    }
    return el && el.parentElement ? el : btn;
  }

  function mountCartPanels() {
    var buttons = document.querySelectorAll('[name="checkout"]');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      if (!btn.parentElement) continue;
      var anchor = mountTarget(btn);
      var host = anchor.parentElement;
      if (!host) continue;
      // One panel per checkout button. The drawer re-renders on every
      // quantity change, so this has to be safe to run repeatedly.
      if (host.querySelector(':scope > [data-ak-cart-region]')) continue;
      var panel = buildCartPanel();
      if (!panel) return;
      host.insertBefore(panel, anchor);
    }
  }

  function watchForCart() {
    mountCartPanels();
    // The cart drawer renders its checkout button after the fact, and re-renders
    // it on every quantity change, so watch rather than mount once.
    if (!('MutationObserver' in window)) return;
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      window.setTimeout(function () {
        pending = false;
        mountCartPanels();
      }, 120);
    }).observe(document.body, { childList: true, subtree: true });
  }

  // ---- boot ---------------------------------------------------------------

  detectCountry().then(function (country) {
    if (!country) return;

    var servedHere = config.serves.indexOf(country) !== -1;
    var servedThere = config.other.serves.indexOf(country) !== -1;

    // Only speak up when the other store actually serves this visitor. A
    // shopper in a country neither store ships to gets nothing, because there
    // is nowhere useful to send them.
    if (servedHere || !servedThere) return;

    fillCopy();

    var saved = readStore(STATE_KEY);
    show(saved && saved.s === 'strip' ? 'strip' : 'expanded');

    if (config.page.template === 'cart' || (config.cart && config.cart.length)) {
      watchForCart();
    }
  });
})();
